import { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";

type Props = { industry: string };

const empty = { firstName: "", lastName: "", phone: "", email: "", businessName: "", message: "" };

/** Short quote form that posts to /api/contact (stored + emailed to Chris). */
export function IndustryQuoteForm({ industry }: Props) {
  const [f, setF] = useState(empty);
  const [consent, setConsent] = useState(false);
  const [hp, setHp] = useState("");
  const [loadedAt, setLoadedAt] = useState(0);
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [err, setErr] = useState("");

  useEffect(() => setLoadedAt(Date.now()), []);

  const set = (k: keyof typeof empty) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setF((p) => ({ ...p, [k]: e.target.value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!consent) return setErr("Please check the consent box.");
    setStatus("sending");
    setErr("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...f,
          message: f.message.trim().length >= 5 ? f.message : `${industry} marketing quote request`,
          consent: true,
          website: hp,
          formLoadedAt: loadedAt,
          topic: `${industry} quote`,
          referringPage: typeof window !== "undefined" ? window.location.pathname : "",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Something went wrong. Please call (719) 419-3935.");
      setStatus("done");
    } catch (e2) {
      setStatus("error");
      setErr(e2 instanceof Error ? e2.message : "Something went wrong.");
    }
  }

  const input =
    "h-12 w-full rounded-md border border-border bg-background px-4 text-base text-foreground focus:border-brand-gold focus:outline-none focus:ring-2 focus:ring-brand-gold/40";

  return (
    <section id="quote" className="bg-muted/40 py-16 lg:py-24">
      <div className="mx-auto max-w-3xl px-5 lg:px-8">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">Request a Quote</span>
        <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
          Get a {industry.toLowerCase()} marketing quote
        </h2>
        <p className="mt-3 text-muted-foreground">
          Tell Chris about your {industry.toLowerCase()} business. He'll reply personally from our Colorado Springs office.
        </p>
        {status === "done" ? (
          <div className="mt-8 flex items-start gap-3 rounded-2xl border border-border bg-card p-6">
            <CheckCircle2 className="h-6 w-6 shrink-0 text-brand-gold-deep" aria-hidden />
            <p className="font-semibold text-foreground">Got it — Chris will be in touch about your quote.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 grid gap-4 rounded-2xl border border-border bg-card p-6 sm:grid-cols-2">
            <label className="text-sm font-semibold text-foreground">First name
              <input required maxLength={80} value={f.firstName} onChange={set("firstName")} className={`${input} mt-1`} autoComplete="given-name" />
            </label>
            <label className="text-sm font-semibold text-foreground">Last name
              <input required maxLength={80} value={f.lastName} onChange={set("lastName")} className={`${input} mt-1`} autoComplete="family-name" />
            </label>
            <label className="text-sm font-semibold text-foreground">Phone
              <input required type="tel" maxLength={30} value={f.phone} onChange={set("phone")} className={`${input} mt-1`} autoComplete="tel" />
            </label>
            <label className="text-sm font-semibold text-foreground">Email
              <input required type="email" maxLength={255} value={f.email} onChange={set("email")} className={`${input} mt-1`} autoComplete="email" />
            </label>
            <label className="text-sm font-semibold text-foreground sm:col-span-2">Business name
              <input required maxLength={150} value={f.businessName} onChange={set("businessName")} className={`${input} mt-1`} autoComplete="organization" />
            </label>
            <label className="text-sm font-semibold text-foreground sm:col-span-2">What do you need help with?
              <textarea maxLength={2000} rows={4} value={f.message} onChange={set("message")} className={`${input} mt-1 h-auto py-3`} />
            </label>
            <input type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" aria-hidden />
            <label className="flex items-start gap-2 text-sm text-muted-foreground sm:col-span-2">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
              I agree to be contacted about my quote by phone, text or email.
            </label>
            {err && <p className="text-sm font-semibold text-destructive sm:col-span-2" role="alert">{err}</p>}
            <button
              type="submit"
              disabled={status === "sending"}
              className="h-12 rounded-full bg-brand-gold px-6 font-bold text-foreground disabled:opacity-60 sm:col-span-2"
            >
              {status === "sending" ? "Sending…" : "Request a Quote"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
